import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';

import { Config } from '../../common/config';
import {
  formatGiftSerialDisplay,
  toGiftSerialSearchValue,
} from '../../utils/formatGiftSerial';
import AlertModal from '../../components/alertModal';
import LeftMenu from '../../components/leftMenu';
import Pagination from '../../components/pagination';
import {
  formatNumNumber,
  formatNumber,
  getErrorMessage,
  isSuccessResponse,
} from '../../components/utils';
import translations from '../../data/translation';
import LocaleDateInput from '../../components/LocaleDateInput';
import useAxiosFetch from '../../hooks/useAxiosFetch'; // 경로는 실제 파일 위치에 맞게 수정
import useStoreNo from '../../hooks/useStoreNo';

// [2026-10-28] 번역값 "--조회조건--" 의 대시 장식은 라벨에서 뺀다(드롭다운 값과 같은 글자가 두 번 보이던 것)
const plainLabel = label => String(label || '').replace(/^-+|-+$/g, '').trim();

export default function GiftSales() {
  const { fetchData } = useAxiosFetch();

  const navigate = useNavigate();
  const userInfo = useSelector(state => state.userInfo);
  const storeNo = useStoreNo();

  const location = useLocation();
  const receivedData = location.state || {};
  const giftNo = receivedData.giftNo;
  const language = useSelector(state => state.language.language); // redux에서 언어 상태 가져오기
  const t = translations[language]; // 현재 언어에 따른 번역 객체 가져오기

  const [modalOpen, setModalOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState({
    isCancel: false,
    isConfirm: true,
    isMessage: true,
    headerMsg: '',
    actionFlag: '',
    mainMsg: '',
    action: '',
    isPercent: false,
  });
  const [searchCondition, setSearchCondition] = useState({
    flag: false,
    pageSize: Config.PAGE_SIZE_LIST[0],
    sortOrd: '',
    searchCon: '',
    startDttm: '',
    endDttm: '',
    conOrd: '',
    giftNo: giftNo ? String(giftNo) : '',
    giftIdOrd: '',
    serial: '',
    paymentCon: '',
    usageCon: '',
    depositCon: '',
    refundCon: '',
    gifter: '',
    giftee: '',
  });
  const [, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [, setMainData] = useState([]);
  const [lastData, setLastData] = useState({});

  const closeModal = () => {
    setModalOpen(false);
  };

  const doModal = () => {
    setModalOpen(false);
    if (modalInfo.actionFlag === 'login') {
      navigate('/login');
    }
  };
  // JWT refresh handled centrally in useAxiosFetch

  const getData = useCallback(async () => {
    // ("===== searchCondition.searchSection : "+JSON.stringify(searchCondition));
    try {
      // let params = "?storeNo=4";
      let params = '?pageNo=' + currentPage;
      params += '&pageSize=' + searchCondition.pageSize;
      if (searchCondition.sortOrd.length > 0) {
        params += '&sortOrd=' + searchCondition.sortOrd;
      }
      if (searchCondition.searchCon.length > 0) {
        params += '&searchCon=' + searchCondition.searchCon;
      }
      if (searchCondition.startDttm.length > 0) {
        params += '&startDttm=' + searchCondition.startDttm;
      }
      if (searchCondition.endDttm.length > 0) {
        params += '&endDttm=' + searchCondition.endDttm;
      }
      if (searchCondition.conOrd.length > 0) {
        params += '&conOrd=' + searchCondition.conOrd;
      }
      if (searchCondition.giftNo.length > 0) {
        params += '&giftNo=' + encodeURIComponent(searchCondition.giftNo);
      }
      if (searchCondition.giftIdOrd.length > 0) {
        params += '&giftIdOrd=' + searchCondition.giftIdOrd;
      }
      // API 파라미터명은 version (2026-06 serial→version 변경). 상품명에 +,& 등이 있어 인코딩 필수.
      if (searchCondition.serial.trim().length > 0) {
        params +=
          '&version=' +
          encodeURIComponent(toGiftSerialSearchValue(searchCondition.serial));
      }
      if (searchCondition.paymentCon.length > 0) {
        params += '&paymentCon=' + searchCondition.paymentCon;
      }
      if (searchCondition.usageCon.length > 0) {
        params += '&usageCon=' + searchCondition.usageCon;
      }
      if (searchCondition.depositCon.length > 0) {
        params += '&depositCon=' + searchCondition.depositCon;
      }
      if (searchCondition.refundCon.length > 0) {
        params += '&refundCon=' + searchCondition.refundCon;
      }
      if (searchCondition.gifter.length > 0) {
        params += '&gifter=' + searchCondition.gifter;
      }
      if (searchCondition.giftee.length > 0) {
        params += '&giftee=' + searchCondition.giftee;
      }

      const ret = await fetchData({
        url:
          Config.API_SERVER + `gift/salesStatus/${userInfo.storeNo}` + params,
        method: 'get',
      });
      if (isSuccessResponse(ret) && ret?.data?.data) {
        const count = ret.data.data.total ?? 0;
        const tp = Math.max(1, Math.ceil(count / searchCondition.pageSize));
        const list0 = ret.data.data.list?.[0] ?? [];

        setTotalPage(tp);
        setTotal(count);

        setData(Array.isArray(list0) ? list0 : []);
        setMainData(ret.data.data.list?.slice(0, -1) ?? []);
        setLastData(ret.data.data.list?.[1]?.[0] ?? {});
      } else if (!isSuccessResponse(ret)) {
        setData([]);
        setModalInfo(prev => ({
          ...prev,
          mainMsg: getErrorMessage(ret),
        }));
        setModalOpen(true);
      }
      setSearchCondition(prev => ({ ...prev, flag: false }));
    } catch (error) {
      setData([]);
      setLastData({});
      setModalInfo(prev => ({
        ...prev,
        mainMsg:
          getErrorMessage(error?.response) ||
          (language === 'ko'
            ? t.searchLoadFailed
            : 'Failed to load search results.'),
      }));
      setModalOpen(true);
      console.error('Error getData:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    searchCondition.pageSize,
    searchCondition.sortOrd,
    searchCondition.searchCon,
    searchCondition.startDttm,
    searchCondition.endDttm,
    searchCondition.conOrd,
    searchCondition.giftNo,
    searchCondition.giftIdOrd,
    searchCondition.serial,
    searchCondition.paymentCon,
    searchCondition.usageCon,
    searchCondition.depositCon,
    searchCondition.refundCon,
    searchCondition.gifter,
    searchCondition.giftee,
    searchCondition.flag,
    userInfo.storeNo,
  ]);

  // 상점주 쪽 액션(사용취소)은 실제로 상점에 돈이 걸리기 전(payoutYn='Y' 되기 전)까지만
  // 허용한다 — Status.js(모바일)의 동일 가드와 같은 기준. '정산대기'(settlementYn='Y')는
  // 토스 쪽 기록만 생겼을 뿐 상점 지급 전이라 허용, '정산집계완료' 이후부터 차단한다.
  // 화이트리스트 방식이라 새 라벨이 추가돼도 기본은 차단(fail-closed)이다.
  const OWNER_ACTION_ALLOWED_SETTLEMENT_STATES = ['입금대기', '정산대기'];
  const canCancelUsage = item =>
    !['PS021', 'PS024'].includes(item?.refundState) &&
    OWNER_ACTION_ALLOWED_SETTLEMENT_STATES.includes(item?.settlementStateDesc);

  // settlementStateDesc → 배지 색상. Status.js(모바일)의 getSettlementBadgeClass와
  // 동일하게 4단계(회색/노랑/초록/빨강)로 맞춘다 — '입금대기'까지 노랑으로 뭉뚱그리면
  // 모바일과 색이 어긋난다.
  const getSettlementBadgeSuffix = settlementStateDesc => {
    if (settlementStateDesc === '환불/회수') return 'refund';
    if (settlementStateDesc === '정산집계완료' || settlementStateDesc === '상점지급완료') return 'DS002';
    if (settlementStateDesc === '정산대기') return 'DS001';
    return 'default';
  };

  const handleUsageCancel = useCallback(
    async (userGiftNo, rowIndex) => {
      const result = await Swal.fire({
        icon: 'question',
        title: t.usageCancel,
        text: t.confirmUsageCancel,
        showCancelButton: true,
        confirmButtonText: language === 'ko' ? '확인' : 'Confirm',
        cancelButtonText: language === 'ko' ? '취소' : 'Cancel',
      });
      if (!result.isConfirmed) return;
      try {
        const response = await fetchData({
          method: 'PATCH',
          url: `${Config.API_SERVER}user-gift/${userGiftNo}`,
        });
        const ok =
          response?.status >= 200 &&
          response?.status < 300 &&
          (response?.data?.resultCode === 200 || !response?.data?.resultCode);
        if (ok) {
          setData(prev =>
            prev.map((r, i) =>
              i === rowIndex
                ? { ...r, usageState: 'GS001', usageStateDesc: language === 'ko' ? '미사용' : 'Unused' }
                : r
            )
          );
          await Swal.fire({
            icon: 'success',
            title: language === 'ko' ? '완료' : 'Done',
            text: t.usageCancelSuccess,
            confirmButtonText: language === 'ko' ? '확인' : 'Confirm',
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: language === 'ko' ? '오류' : 'Error',
            text: t.usageCancelFailed,
            confirmButtonText: language === 'ko' ? '확인' : 'Confirm',
          });
        }
      } catch {
        Swal.fire({
          icon: 'error',
          title: language === 'ko' ? '오류' : 'Error',
          text: t.usageCancelFailed,
          confirmButtonText: language === 'ko' ? '확인' : 'Confirm',
        });
      }
    },
    [fetchData, t, language]
  );

  const pageChange = page => {
    setCurrentPage(page);
  };

  const getClear = () => {
    setSearchCondition({
      ...searchCondition,
      sortOrd: '',
      searchCon: '',
      startDttm: '',
      endDttm: '',
      conOrd: '',
      giftNo: '',
      giftIdOrd: '',
      serial: '',
      paymentCon: '',
      usageCon: '',
      depositCon: '',
      refundCon: '',
      gifter: '',
      giftee: '',
    });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchCondition.flag]);

  return (
    <>
      <AlertModal
        open={modalOpen}
        close={closeModal}
        action={doModal}
        header={modalInfo.headerMsg}
        isConfirm={modalInfo.isConfirm}
        isMessage={modalInfo.isMessage}
        isCancel={modalInfo.isCancel}
      >
        {modalInfo.mainMsg}
      </AlertModal>

      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>
              {t.home}
            </NavLink>
          </li>
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>
              {t.giftManagement}
            </NavLink>
          </li>
          <li>{t.salesStatus}</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="gift" />

          <section className="rightcontant_box">
            <h2 className="tit_01">{t.salesStatus}</h2>

            <article className="search_card">
              <div className="search_row">
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.searchCondition)}</span>
                  <select
                    value={searchCondition.searchCon}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        searchCon: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '선택' : 'Select'}</option>
                    <option value="C1">{t.paymentDate}</option>
                    <option value="C2">{t.usageDate}</option>
                    <option value="C3">{t.depositDate}</option>
                    <option value="C4">{t.refundRequestDate}</option>
                    <option value="C5">{t.refundCompletionDate}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.searchPeriod)}</span>
                  <LocaleDateInput
                    value={searchCondition.startDttm}
                    onChange={val =>
                      setSearchCondition({
                        ...searchCondition,
                        startDttm: val,
                      })
                    }
                    placeholderText={t.startDate}
                  />
                  <span className="date_sep">~</span>
                  <LocaleDateInput
                    value={searchCondition.endDttm}
                    onChange={val =>
                      setSearchCondition({
                        ...searchCondition,
                        endDttm: val,
                      })
                    }
                    placeholderText={t.endDate}
                  />
                </div>
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.searchOrder)}</span>
                  <select
                    value={searchCondition.conOrd}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        conOrd: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '기본순' : 'Default'}</option>
                    <option value="desc">{t.descendingOrder}</option>
                    <option value="asc">{t.ascendingOrder}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.giftId)}</span>
                  <select
                    value={searchCondition.giftIdOrd}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        giftIdOrd: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '기본순' : 'Default'}</option>
                    <option value="desc">{t.descendingOrder}</option>
                    <option value="asc">{t.ascendingOrder}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.paymentMethod)}</span>
                  <select
                    value={searchCondition.paymentCon}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        paymentCon: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '전체' : 'All'}</option>
                    <option value="PM001">{t.tossPay}</option>
                    <option value="PM002">{t.naverPay}</option>
                    <option value="PM003">{t.kakaoPay}</option>
                    <option value="PM004">{t.payco}</option>
                    <option value="PM005">{t.creditCard}</option>
                  </select>
                </div>
              </div>
              <div className="search_row">
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.usageInfo)}</span>
                  <select
                    value={searchCondition.usageCon}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        usageCon: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '전체' : 'All'}</option>
                    <option value="GS001">{t.unused}</option>
                    <option value="GS002">{t.completed}</option>
                    <option value="GS003">{t.errorUsed}</option>
                    <option value="GS004">{t.expired}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.depositInfo)}</span>
                  <select
                    value={searchCondition.depositCon}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        depositCon: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '전체' : 'All'}</option>
                    <option value="DS001">{t.notDeposited}</option>
                    <option value="DS002">{t.deposited}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.refundInfo)}</span>
                  <select
                    value={searchCondition.refundCon}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        refundCon: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '전체' : 'All'}</option>
                    <option value="PS021">{t.refundRequest}</option>
                    <option value="PS024">{t.refundCompleted}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.giftSerialOrName)}</span>
                  <input
                    type="text"
                    placeholder={t.giftSerialOrName}
                    maxLength={80}
                    value={searchCondition.serial}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        serial: event.target.value,
                      })
                    }
                  />
                </div>
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.gifter)}</span>
                  <input
                    type="text"
                    placeholder={t.gifter}
                    maxLength={20}
                    value={searchCondition.gifter}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        gifter: event.target.value,
                      })
                    }
                  />
                </div>
                <div className="search_field">
                  <span className="field_label">{plainLabel(t.giftee)}</span>
                  <input
                    type="text"
                    placeholder={t.giftee}
                    maxLength={20}
                    value={searchCondition.giftee}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        giftee: event.target.value,
                      })
                    }
                  />
                </div>
                <div className="search_btns">
                  <button
                    type="button"
                    className="button_05 button_black"
                    onClick={getData}
                  >
                    {t.search}
                  </button>
                  <button
                    type="button"
                    className="button_05 button_gray_line"
                    onClick={getClear}
                  >
                    {t.reset}
                  </button>
                </div>
              </div>
            </article>

            <table className="table_box_01">
              <colgroup>
                <col width="55px" />
                <col width="210px" />
                <col width="145px" />
                <col width="145px" />
                <col width="100px" />
                <col width="130px" />
                <col width="80px" />
                <col width="80px" />
                <col width="150px" />
              </colgroup>

              <thead>
                <tr>
                  <th>{t.no}</th>
                  <th>{t.giftSerial}</th>
                  <th>{t.gifter}</th>
                  <th>{t.giftee}</th>
                  <th>{t.paymentDate}</th>
                  <th>{t.paymentMethod}</th>
                  <th>{t.usageDetails}</th>
                  <th>{t.depositDetails}</th>
                  <th>{t.refundDetails}</th>
                </tr>
              </thead>

              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#666',
                      }}
                    >
                      {language === 'ko'
                        ? t.noSearchResult
                        : 'No search results.'}
                    </td>
                  </tr>
                ) : (
                  data.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td style={{ fontFamily: 'inherit' }}>
                        {formatGiftSerialDisplay(item)}
                      </td>
                      <td>
                        {item.gifterEmail ? (
                          item.gifterEmail
                        ) : (
                          <span className="cell-empty">—</span>
                        )}
                      </td>
                      <td>
                        {item.gifteeEmail && item.gifteeEmail !== ' ' ? (
                          item.gifteeEmail
                        ) : (
                          <span className="cell-empty">—</span>
                        )}
                      </td>
                      {/* 결제일 */}
                      <td>
                        {item.paymentDttm ? (
                          item.paymentDttm.slice(0, 10)
                        ) : (
                          <span className="cell-empty">—</span>
                        )}
                      </td>
                      {/* 결제수단 - 금액 + 수단 (토스페이/네이버페이/카카오페이/페이코/신용카드) */}
                      <td>
                        {item.price != null || item.payMethodDesc ? (
                          <div className="payment-method-cell">
                            {item.price != null && (
                              <span className="payment-method-amount">
                                {formatNumNumber(item.price)}
                                {t.currency}
                              </span>
                            )}
                            {item.payMethodDesc && (
                              <span
                                className={`payment-method-badge payment-method-badge--${
                                  {
                                    토스페이: 'toss',
                                    네이버페이: 'naver',
                                    카카오페이: 'kakao',
                                    페이코: 'payco',
                                    신용카드: 'card',
                                  }[item.payMethodDesc] || 'default'
                                }`}
                              >
                                {t.payMethodDesc?.[item.payMethodDesc] ||
                                  item.payMethodDesc}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="cell-empty">—</span>
                        )}
                      </td>
                      {/* 사용정보 - usageStateDesc: 미수신/미사용/사용완료 등 */}
                      <td>
                        <span
                          className={`state-badge usage-${
                            item.usageState || ''
                          }`}
                        >
                          {t.stateDesc?.[item.usageStateDesc] ||
                            item.usageStateDesc}
                        </span>
                        {['GS002', 'GS003'].includes(item.usageState) &&
                          item.userGiftNo &&
                          canCancelUsage(item) && (
                          <div style={{ marginTop: 4 }}>
                            <button
                              type="button"
                              className="button_05 button_gray_line"
                              style={{ fontSize: '11px', padding: '2px 8px' }}
                              onClick={() => handleUsageCancel(item.userGiftNo, index)}
                            >
                              {t.usageCancel}
                            </button>
                          </div>
                        )}
                      </td>
                      {/* 입금정보 — 정산 진행 상태 라벨(settlementStateDesc) */}
                      <td>
                        {item.settlementStateDesc && (
                          <span
                            className={`state-badge deposit-${getSettlementBadgeSuffix(
                              item.settlementStateDesc
                            )}`}
                          >
                            {item.settlementStateDesc}
                          </span>
                        )}
                      </td>
                      {/* 환불정보 */}
                      <td>
                        {item.refundState ? (
                          <>
                            <span
                              className={`state-badge refund-${
                                item.refundState || ''
                              }`}
                            >
                              {t.stateDesc[item.refundStateDesc] ||
                                item.refundStateDesc}
                            </span>
                            {(item.accountNo || item.accountHolder) && (
                              <>
                                <br />
                                {t.accountNo}: {item.accountNo || ''} <br />
                                {t.accountHolder}: {item.accountHolder || ''}
                              </>
                            )}
                          </>
                        ) : (
                          <span className="cell-empty">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <article className="table_search_box">
              <ul className="table_02">
                <li>
                  {t.totalCount} : {formatNumNumber(lastData.count)}
                  {t.count}
                </li>
                <li>
                  {t.paymentAmount} : {formatNumNumber(lastData.totalPayAmount)}
                  {t.currency}
                </li>
                <li>
                  {t.usageAmount} : {formatNumNumber(lastData.totalUsageAmount)}
                  {t.currency}
                </li>
                <li>
                  {t.depositAmount} :{' '}
                  {formatNumNumber(lastData.totalDepositAmount)}
                  {t.currency}
                </li>
                <li>
                  {t.refundAmount} :{' '}
                  {formatNumNumber(lastData.totalRefundAmount)}
                  {t.currency}
                </li>
                <li>
                  {t.serviceFee} :{' '}
                  {formatNumNumber(
                    parseInt(formatNumber(lastData?.totalServiceFee))
                  )}
                  {t.currency}
                </li>
                <li>
                  {t.saleAmount} :{' '}
                  {formatNumNumber(
                    parseInt(formatNumber(lastData?.totalSaleAmount))
                  )}
                  {t.currency}
                </li>
              </ul>
            </article>
            {/* <article className="page_box"> */}
            <ul className="page_box">
              <Pagination totalPage={totalPage} onPageChange={pageChange} />
            </ul>
            {/* </article> */}
          </section>
        </section>
      </main>
    </>
  );
}
